import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { getSettingQueryKey } from './get-setting';
import { getSettingsQueryKey } from './get-settings';

export interface SetSettingsDto<T = unknown> {
  key: string;
  value: T;
}

export const setSettings = async (
  orgId: string,
  projectId: string | undefined,
  key: string,
  value: unknown,
): Promise<{ success: boolean }> => {
  if (projectId) {
    const { data } = await api.put<SetSettingsDto, ApiResponse<{ success: boolean }>>(
      `/orgs/${orgId}/projects/${projectId}/settings/${key}`,
      { value },
    );
    return data;
  }
  const { data } = await api.put<SetSettingsDto, ApiResponse<{ success: boolean }>>(
    `/orgs/${orgId}/settings/${key}`,
    { value },
  );
  return data;
};

interface UseSetSettingsOptions {
  mutationConfig?: MutationConfig<(settings: SetSettingsDto) => Promise<{ success: boolean }>>;
}

export const useSetSettings = ({ mutationConfig }: UseSetSettingsOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');
  const { projectId } = useScopeIds();

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: getSettingsQueryKey(orgId) });
      void queryClient.invalidateQueries({ queryKey: getSettingQueryKey(orgId, projectId) });
      notifications.success('settings.set.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('settings.set.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (settings: SetSettingsDto) =>
      setSettings(orgId, projectId, settings.key, settings.value),
  });
};
